import {
  addressService,
  AddressServiceLanguage,
  AutoCompleteCitiesResponse,
} from '../services/addressService';
import { CountryConfig } from '../types/apiConfig';

type ValidateAddressProps = {
  ui_locales: AddressServiceLanguage;
  address: {
    postalCode?: string;
    city?: string;
    street?: string;
    houseNumber?: string;
    country?: string;
  };
  countryConfig: CountryConfig;
};

type ValidateAddressResult = {
  isValidCity: boolean;
  isValidPostalCode: boolean;
  isValidMatchPostalCodeCity: boolean;
  isValidPostalCodeOrCity: boolean;
  isValidStreet: boolean;
  isValidHouseNumber: boolean;
  isValidCountry: boolean;
};

export async function validateAddress(props: ValidateAddressProps): Promise<ValidateAddressResult> {
  const { countryConfig, ui_locales, address } = props;
  const cityInput = address.city;
  const postalCodeInput = address.postalCode;

  let citiesResult: AutoCompleteCitiesResponse | undefined;
  let postalCodesResult: AutoCompleteCitiesResponse | undefined;

  let isValidCity = false;
  let isValidPostalCode = false;
  let isValidMatchPostalCodeCity = false;
  let isValidPostalCodeOrCity = false;
  let isValidStreet = false;
  let isValidCountry = false;

  // Country validation
  if (address.country) {
    const countries = await addressService.getCountries({ language: ui_locales });
    isValidCountry = countries.some((c) => c.IsoCode === address.country);
  }
  // City validation
  if (cityInput && props.countryConfig.validation.validateCity) {
    citiesResult = await addressService.autoCompleteCities({
      language: ui_locales,
      countryISO: countryConfig.countryIso,
      key: cityInput,
    });
    isValidCity = citiesResult.Cities.some(
      (city) => city.Name.toLowerCase() === cityInput.toLowerCase()
    );
  }
  // Postal code validation
  if (postalCodeInput && props.countryConfig.validation.validatePostcode) {
    postalCodesResult = await addressService.autoCompleteCities({
      language: ui_locales,
      countryISO: countryConfig.countryIso,
      key: postalCodeInput,
    });
    isValidPostalCode = postalCodesResult.Cities.some(
      (city) => city.PostalCode.toLowerCase() === postalCodeInput.toLowerCase()
    );
  }
  isValidPostalCodeOrCity = isValidCity || isValidPostalCode;
  // Postal code & city match validation
  if (
    cityInput &&
    postalCodeInput &&
    postalCodesResult &&
    props.countryConfig.validation.matchPostcodeAndCity
  ) {
    isValidMatchPostalCodeCity = postalCodesResult.Cities.reduce(
      (prev, curr) =>
        prev ||
        (curr.Name.toLowerCase() === cityInput.toLowerCase() &&
          curr.PostalCode.toLowerCase() === postalCodeInput.toLowerCase()),
      false
    );
  }
  // Street validation
  if (postalCodeInput && isValidPostalCode && address.street) {
    const streetsResult = await addressService.autoCompleteStreets({
      language: ui_locales,
      countryISO: countryConfig.countryIso,
      postalCode: postalCodeInput,
      key: address.street,
    });
    isValidStreet = streetsResult.Streets.some(
      (street) => street.Name.toLowerCase() === address.street?.toLowerCase()
    );
  }
  let isValidHouseNumber = false;
  if (
    cityInput &&
    postalCodeInput &&
    isValidPostalCode &&
    isValidStreet &&
    address.street &&
    address.houseNumber
  ) {
    const addressResult = await addressService.validateAddress({
      addressToValidate: {
        street: {
          name: address.street,
        },
        city: {
          postalCode: postalCodeInput,
          name: cityInput,
        },
        houseNumber: address.houseNumber,
        countryISOCode: countryConfig.countryIso,
      },
    });
    if (props.countryConfig.validation.validateHouseNumber)
      isValidHouseNumber = addressResult.ValidationResult.ValidationPerField.some(
        (field) => field.Key === 'number' && field.Value === true
      );
  }

  return {
    isValidCity,
    isValidPostalCode,
    isValidMatchPostalCodeCity,
    isValidPostalCodeOrCity,
    isValidStreet,
    isValidHouseNumber,
    isValidCountry,
  };
}
